import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';

const TooltipContext = createContext();

export const useTooltip = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  return context;
};

export const TooltipProvider = ({ children }) => {
  const [openTooltip, setOpenTooltip] = useState(null);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openTooltip) {
        closeTooltipHandler();
      }
    };

    const handleMouseLeave = () => {
      closeTooltipHandler();
      clearHoveredIconHandler();
    };

    const handleMouseMove = () => {
      if (openTooltip && tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = setTimeout(() => {
          setOpenTooltip(null);
        }, 5000);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousemove', handleMouseMove);
      cleanup();
    };
  }, [openTooltip]);

  const openTooltipHandler = (tooltipId) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (openTooltip && openTooltip !== tooltipId) {
      setOpenTooltip(null);
      setOpenTooltip(tooltipId);
      tooltipTimeoutRef.current = setTimeout(() => {
        setOpenTooltip(null);
      }, 5000);
    } else if (openTooltip === tooltipId) {
      setOpenTooltip(null);
    } else {
      setOpenTooltip(tooltipId);
      tooltipTimeoutRef.current = setTimeout(() => {
        setOpenTooltip(null);
      }, 5000);
    }
  };

  const closeTooltipHandler = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    setOpenTooltip(null);
  };

  const setHoveredIconHandler = (iconId) => {
    setHoveredIcon(iconId);
  };

  const clearHoveredIconHandler = () => {
    setHoveredIcon(null);
  };

  const cleanup = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  const value = {
    openTooltip,
    hoveredIcon,
    openTooltipHandler,
    closeTooltipHandler,
    setHoveredIconHandler,
    clearHoveredIconHandler,
    cleanup,
  };

  return (
    <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
  );
};
